import React, { useMemo, useState } from "react";
import {
  EuiComboBox,
  EuiComboBoxOptionOption,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
} from "@elastic/eui";

import { plantsArray } from "./plants";
import { animalsArray } from "./animals";

interface NatureSelectorProps {
  onAnimalChange?: (value: string | null) => void;
  onPlantChange?: (value: string | null) => void;
}

const toOptions = (arr: string[]): EuiComboBoxOptionOption[] =>
  arr.map((item) => ({
    label: item.charAt(0).toUpperCase() + item.slice(1),
    value: item,
  }));

const animalOptions = toOptions(animalsArray);
const plantOptions = toOptions(plantsArray);

const NatureSelector: React.FC<NatureSelectorProps> = ({
  onAnimalChange,
  onPlantChange,
}) => {
  const [selectedAnimal, setSelectedAnimal] = useState<
    EuiComboBoxOptionOption[]
  >([]);
  const [selectedPlant, setSelectedPlant] = useState<EuiComboBoxOptionOption[]>(
    [],
  );

  const [animalSearch, setAnimalSearch] = useState("");
  const [plantSearch, setPlantSearch] = useState("");

  const handleAnimalChange = (selected: EuiComboBoxOptionOption[]) => {
    setSelectedAnimal(selected);
    onAnimalChange?.((selected[0]?.value as string) ?? null);
  };

  const handlePlantChange = (selected: EuiComboBoxOptionOption[]) => {
    setSelectedPlant(selected);
    onPlantChange?.((selected[0]?.value as string) ?? null);
  };

  // Prefix ("starts with") search. We pre-filter the options ourselves and set
  // `async` on the combobox so EUI treats them as already filtered instead of
  // applying its own substring match on top (which would show "contains" hits).
  const filteredAnimals = useMemo(() => {
    const q = animalSearch.trim().toLowerCase();
    return q ? animalOptions.filter((o) => o.label.toLowerCase().startsWith(q)) : animalOptions;
  }, [animalSearch]);

  const filteredPlants = useMemo(() => {
    const q = plantSearch.trim().toLowerCase();
    return q ? plantOptions.filter((o) => o.label.toLowerCase().startsWith(q)) : plantOptions;
  }, [plantSearch]);

  return (
    <EuiFlexGroup>
      <EuiFlexItem>
        <EuiFormRow label="Animal">
          <EuiComboBox
            placeholder="Search for an animal..."
            singleSelection={{ asPlainText: true }}
            async
            options={filteredAnimals}
            selectedOptions={selectedAnimal}
            onChange={handleAnimalChange}
            onSearchChange={(searchValue: string) => setAnimalSearch(searchValue)}
            isClearable
          />
        </EuiFormRow>
      </EuiFlexItem>

      <EuiFlexItem>
        <EuiFormRow label="Plant">
          <EuiComboBox
            placeholder="Search for a plant..."
            singleSelection={{ asPlainText: true }}
            async
            options={filteredPlants}
            selectedOptions={selectedPlant}
            onChange={handlePlantChange}
            onSearchChange={(searchValue: string) => setPlantSearch(searchValue)}
            isClearable
          />
        </EuiFormRow>
      </EuiFlexItem>
    </EuiFlexGroup>
  );
};

export default NatureSelector;
